import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAdminBrands } from '../hooks/api/useAdmin';

interface Brand {
  id: number;
  name: string;
  displayName: string;
  snowflakeIdNum?: number;
}

interface AdminBrandContextType {
  // Selected brand state
  selectedBrandId: number | null;
  selectedBrand: Brand | null;
  setSelectedBrandId: (brandId: number | null) => void;

  // All brands
  brands: Brand[];
  isLoadingBrands: boolean;

  // Helper
  isAllBrandsView: boolean;
}

const AdminBrandContext = createContext<AdminBrandContextType | undefined>(undefined);

const STORAGE_KEY = 'admin_selected_brand_id';

interface AdminBrandProviderProps {
  children: React.ReactNode;
}

export const AdminBrandProvider: React.FC<AdminBrandProviderProps> = ({ children }) => {
  // Load initial state from localStorage
  const [selectedBrandId, setSelectedBrandIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  });

  // Fetch all brands
  const { data: brandsData, isLoading: isLoadingBrands } = useAdminBrands();

  // Transform brands data
  const brands: Brand[] = useMemo(() => {
    if (!brandsData?.brands) return [];
    return brandsData.brands.map((b) => ({
      id: b.id,
      name: b.name,
      displayName: b.displayName || b.name,
      snowflakeIdNum: b.snowflakeIdNum || b.id,
    }));
  }, [brandsData]);

  // Get the selected brand object
  const selectedBrand = useMemo(() => {
    if (!selectedBrandId) return null;
    return brands.find((b) => b.id === selectedBrandId) || null;
  }, [selectedBrandId, brands]);

  // Persist selection to localStorage. Stable identity so consumers that depend
  // on `value` don't re-render just because the provider re-ran.
  const setSelectedBrandId = React.useCallback((brandId: number | null) => {
    setSelectedBrandIdState(brandId);
    if (brandId === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, brandId.toString());
    }
  }, []);

  // Validate stored brand still exists
  useEffect(() => {
    if (selectedBrandId && brands.length > 0) {
      const brandExists = brands.some((b) => b.id === selectedBrandId);
      if (!brandExists) {
        setSelectedBrandId(null);
      }
    }
  }, [selectedBrandId, brands, setSelectedBrandId]);

  const value = useMemo<AdminBrandContextType>(
    () => ({
      selectedBrandId,
      selectedBrand,
      setSelectedBrandId,
      brands,
      isLoadingBrands,
      isAllBrandsView: selectedBrandId === null,
    }),
    [selectedBrandId, selectedBrand, setSelectedBrandId, brands, isLoadingBrands],
  );

  return (
    <AdminBrandContext.Provider value={value}>
      {children}
    </AdminBrandContext.Provider>
  );
};

export function useAdminBrandContext(): AdminBrandContextType {
  const context = useContext(AdminBrandContext);
  if (context === undefined) {
    throw new Error('useAdminBrandContext must be used within an AdminBrandProvider');
  }
  return context;
}

// Alias for convenience
export const useAdminBrand = useAdminBrandContext;

export default AdminBrandContext;
